import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { chatApi, authApi } from '../services/api';
import { resolvePortalAccessUrl } from '../lib/portalAccessUrl';
import { promptAppInstall } from '../lib/pwaInstall';
import { resolveMentorFromBio } from '../lib/voiceProfile';
import {
  cancelSpeechPlayback,
  resetAudioPlaybackGate,
  speakScriptWithElevenLabs,
  SPOKEN_TEXT_ATTRS,
} from '../lib/speechPlayback';
import { resolveWorkspaceScreenId, MENTEE_KEY_STORAGE, normalizeClientKey } from '../lib/screenRegistry';
import './Workspace.css';

const LOGO = encodeURI('/Assets/logo/Mentology Logo.png');
const DEFAULT_MENTOR_VOICE_ID = 's116Gqp9aiAQAe29Albd';
const WPM = 45;
const MS_PER_CHAR = ((60 / WPM) * 1000) / 5;

interface Message {
  role: string;
  content: string;
  proteusMode?: string;
}

interface Bio {
  preferred_name?: string;
  name?: string;
  language_target?: string;
  ai_mentor_gender?: string;
  ai_mentor_name?: string;
  ai_mentor_voice_id?: string;
}

const BRIEFING_SCRIPT_TEMPLATE =
  "Welcome to the Briefing Room, [Mentee's Name]. I am your Mentor, [Mentor Name], and I am here to guide you through the mentoring process. In this space, there is no passive listening. I will challenge your baseline assumptions, ask the difficult questions, and hold you accountable to your own logic. You provide the insight; I provide the mirror. Our work begins with the Core Discovery Interview. To start, you can type or tap your microphone and tell me: what is the primary challenge or insight you bring today, and what goals do you want to achieve from this mentoring relationship?";

function buildBriefingScript(menteeName: string, mentorName: string) {
  return BRIEFING_SCRIPT_TEMPLATE
    .replace("[Mentee's Name]", menteeName)
    .replace('[Mentor Name]', mentorName);
}

function resolveMenteeName(bio: Bio | null, userName?: string) {
  if (bio?.preferred_name?.trim()) return bio.preferred_name.trim();
  if (bio?.name?.trim()) return bio.name.trim().split(/\s+/)[0];
  if (userName?.trim()) {
    const parts = userName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'there';
    if (parts.length === 1) return parts[0];
    const label = parts[0].toLowerCase();
    if (label === 'corporate' || label === 'private') {
      return parts.length >= 3 ? parts[1] : parts[parts.length - 1];
    }
    return parts[0];
  }
  return 'there';
}

function computeSubscriptionDaysLeft() {
  return 28;
}

function readPendingBio(): Bio | null {
  try {
    const pendingRaw = sessionStorage.getItem('mentology.pendingBio');
    return pendingRaw ? JSON.parse(pendingRaw) : null;
  } catch {
    return null;
  }
}

function resolveActiveClientKey(userToken?: string) {
  const stored = sessionStorage.getItem(MENTEE_KEY_STORAGE);
  const candidate = stored || userToken || '';
  return normalizeClientKey(candidate);
}

function isEltMenteeTrack(userToken?: string) {
  return resolveActiveClientKey(userToken).startsWith('ELT-');
}

export default function Workspace() {
  const navigate = useNavigate();
  const { user, chatEnabled, setChatEnabled, logout } = useAuth();
  const workspaceScreenId = resolveWorkspaceScreenId(user?.role);
  const isEltTrack = isEltMenteeTrack(user?.userToken);
  const activeClientKey = resolveActiveClientKey(user?.userToken);
  const [bio, setBio] = useState<Bio | null>(readPendingBio);
  const assignedAiMentorName = resolveMentorFromBio(bio).name;
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [introDisplay, setIntroDisplay] = useState('');
  const [introComplete, setIntroComplete] = useState(false);
  const [localChatEnabled, setLocalChatEnabled] = useState(false);
  const [devBypassActive, setDevBypassActive] = useState(false);
  const [listening, setListening] = useState(false);
  const [reflection, setReflection] = useState('');
  const [synthesis, setSynthesis] = useState<{
    required: boolean;
    report?: { cycle_number: number; report_json: string; completed: number };
    showBilling?: boolean;
  } | null>(null);

  const charIndex = useRef(0);
  const fallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playbackStarted = useRef(false);
  const bioRef = useRef(bio);
  const userRef = useRef(user);
  bioRef.current = bio;
  userRef.current = user;
  const introScriptRef = useRef('');
  const introVoiceIdRef = useRef<string | null>(null);
  const introModelIdRef = useRef<string | null>(null);
  const introLanguageTargetRef = useRef<string | null>(null);
  const spokenThroughRef = useRef(0);
  const speechQueueRef = useRef<string[]>([]);
  const speechPlayingRef = useRef(false);
  const devBypassRef = useRef(false);
  const recognitionRef = useRef<{ stop: () => void; start: () => void } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const aiTextRef = useRef<HTMLDivElement>(null);

  const subscriptionId = user?.userToken || 'PRO-XXXX';
  const subscriptionDaysLeft = computeSubscriptionDaysLeft();

  const drainSpeechQueue = useCallback(async () => {
    if (speechPlayingRef.current) return;

    const voiceId = introVoiceIdRef.current;
    const languageTarget = introLanguageTargetRef.current ?? undefined;
    if (!voiceId) return;

    const next = speechQueueRef.current.shift();
    if (!next) return;

    speechPlayingRef.current = true;
    try {
      await speakScriptWithElevenLabs(next, voiceId, introModelIdRef.current ?? undefined, languageTarget);
    } catch (error) {
      console.warn('[Briefing speech chunk]', error);
    } finally {
      speechPlayingRef.current = false;
      if (speechQueueRef.current.length > 0) {
        void drainSpeechQueue();
      }
    }
  }, []);

  const queueIntroSpeechSlice = useCallback((visibleText: string) => {
    const voiceId = introVoiceIdRef.current;
    if (!voiceId) return;

    const pending = visibleText.slice(spokenThroughRef.current);
    const match = pending.match(/^(.+?[.?!])(?:\s|$)/);
    if (!match?.[1]?.trim()) return;

    spokenThroughRef.current += match[0].length;
    speechQueueRef.current.push(match[1].trim());
    void drainSpeechQueue();
  }, [drainSpeechQueue]);

  const flushIntroSpeechTail = useCallback((script: string) => {
    const voiceId = introVoiceIdRef.current;
    if (!voiceId) return;

    const tail = script.slice(spokenThroughRef.current).trim();
    if (!tail) return;

    spokenThroughRef.current = script.length;
    speechQueueRef.current.push(tail);
    void drainSpeechQueue();
  }, [drainSpeechQueue]);

  const finishIntro = useCallback((script: string) => {
    setIntroDisplay(script);
    setIntroComplete(true);
    flushIntroSpeechTail(script);
  }, [flushIntroSpeechTail]);

  const syncTypewriterFallback = useCallback((script: string) => {
    if (charIndex.current < script.length) {
      const visible = script.slice(0, charIndex.current + 1);
      setIntroDisplay(visible);
      charIndex.current += 1;
      queueIntroSpeechSlice(visible);
      fallbackTimer.current = setTimeout(() => syncTypewriterFallback(script), MS_PER_CHAR);
    } else {
      finishIntro(script);
    }
  }, [finishIntro, queueIntroSpeechSlice]);

  const startIntroPlayback = useCallback((script: string, voiceId?: string | null, modelId?: string | null, languageTarget?: string | null) => {
    if (playbackStarted.current) return;
    playbackStarted.current = true;
    charIndex.current = 0;
    spokenThroughRef.current = 0;
    speechQueueRef.current = [];
    introVoiceIdRef.current = voiceId || DEFAULT_MENTOR_VOICE_ID;
    introModelIdRef.current = modelId ?? null;
    introLanguageTargetRef.current = languageTarget ?? bioRef.current?.language_target ?? null;
    setIntroDisplay('');
    setIntroComplete(false);
    introScriptRef.current = script;

    resetAudioPlaybackGate();
    syncTypewriterFallback(script);
  }, [syncTypewriterFallback]);

  useEffect(() => {
    if (aiTextRef.current) {
      aiTextRef.current.scrollTop = aiTextRef.current.scrollHeight;
    }
  }, [introDisplay]);

  useEffect(() => {
    resetAudioPlaybackGate();
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
      if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
      cancelSpeechPlayback();
      playbackStarted.current = false;
    };
  }, []);

  useEffect(() => {
    const onDevBypass = (event: KeyboardEvent) => {
      if (!event.ctrlKey || !event.shiftKey || event.key.toLowerCase() !== 'b') return;
      event.preventDefault();

      if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
      cancelSpeechPlayback();

      const menteeName = resolveMenteeName(bioRef.current, userRef.current?.name);
      const mentorProfile = resolveMentorFromBio(bioRef.current);
      const script = buildBriefingScript(menteeName, mentorProfile.name);

      devBypassRef.current = true;
      playbackStarted.current = true;
      introScriptRef.current = script;
      introVoiceIdRef.current = mentorProfile.voiceId || DEFAULT_MENTOR_VOICE_ID;
      introModelIdRef.current = mentorProfile.modelId;
      introLanguageTargetRef.current = bioRef.current?.language_target ?? null;

      resetAudioPlaybackGate();
      setIntroDisplay(script);
      setIntroComplete(true);
      setLocalChatEnabled(true);
      setChatEnabled?.(true);
      setDevBypassActive(true);
    };

    window.addEventListener('keydown', onDevBypass);
    return () => window.removeEventListener('keydown', onDevBypass);
  }, [setChatEnabled]);

  useEffect(() => {
    authApi.getBio()
      .then((data) => {
        setBio((current) => ({ ...(current || {}), ...(data.bio || {}) }));
        if (data.bio) {
          sessionStorage.removeItem('mentology.pendingBio');
        }
      })
      .catch(() => {});
    chatApi.getHistory().then((d) => {
      const history = d.messages || [];
      if (history.length > 0) {
        setMessages(history);
        setIntroComplete(true);
      }
    }).catch(() => {});
    chatApi.synthesisStatus().then(setSynthesis).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || devBypassActive || devBypassRef.current) return;

    const menteeName = resolveMenteeName(bio, user?.name);
    const mentorProfile = resolveMentorFromBio(bio);
    const script = buildBriefingScript(menteeName, mentorProfile.name);

    if (introComplete || messages.length > 0) {
      setIntroDisplay(script);
      return;
    }

    if (playbackStarted.current && introScriptRef.current !== script) {
      if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
      cancelSpeechPlayback();
      playbackStarted.current = false;
      charIndex.current = 0;
    }

    const typewriterStalled = playbackStarted.current
      && introScriptRef.current === script
      && charIndex.current < script.length
      && fallbackTimer.current === null;

    if (playbackStarted.current && !typewriterStalled) return;

    if (typewriterStalled) {
      if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
      cancelSpeechPlayback();
      playbackStarted.current = false;
      charIndex.current = 0;
      spokenThroughRef.current = 0;
      speechQueueRef.current = [];
    }

    startIntroPlayback(script, mentorProfile.voiceId, mentorProfile.modelId, bio?.language_target ?? null);
  }, [bio, user, introComplete, messages.length, startIntroPlayback, devBypassActive]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const sendMessage = async () => {
    if (!input.trim() || loading || !canSend) return;
    const userMsg = input.trim();
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const result = await chatApi.sendMessage(userMsg);
      if (result.crisis) {
        setMessages((m) => [...m, { role: 'system', content: result.message }]);
        return;
      }
      setMessages((m) => [...m, {
        role: 'assistant',
        content: result.message.content,
        proteusMode: result.message.proteusMode,
      }]);
    } catch (err: unknown) {
      const e = err as { error?: string };
      setMessages((m) => [...m, { role: 'system', content: e.error || 'Message failed' }]);
    } finally {
      setLoading(false);
    }
  };

  const toggleMic = () => {
    const SpeechRecognitionCtor = (window as Window & {
      SpeechRecognition?: new () => {
        continuous: boolean;
        interimResults: boolean;
        lang: string;
        start: () => void;
        stop: () => void;
        onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
        onend: (() => void) | null;
        onerror: (() => void) | null;
      };
      webkitSpeechRecognition?: new () => {
        continuous: boolean;
        interimResults: boolean;
        lang: string;
        start: () => void;
        stop: () => void;
        onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
        onend: (() => void) | null;
        onerror: (() => void) | null;
      };
    }).SpeechRecognition || (window as Window & { webkitSpeechRecognition?: new () => {
      continuous: boolean;
      interimResults: boolean;
      lang: string;
      start: () => void;
      stop: () => void;
      onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
      onend: (() => void) | null;
      onerror: (() => void) | null;
    } }).webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      alert('Voice input is not supported in this browser.');
      return;
    }

    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-AU';

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join('');
      setInput(transcript);
    };

    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const handleDeleteFiles = async () => {
    setMenuOpen(false);
    if (!confirm('Delete all your files? This cannot be undone.')) return;
    const result = await authApi.deleteData();
    if (!result.success) alert(result.message || result.reason);
    else alert('File deletion initiated.');
  };

  const handleCreateAppIcon = async () => {
    setMenuOpen(false);
    await promptAppInstall();
  };

  const handleShowQrCode = () => {
    setMenuOpen(false);
    setQrModalOpen(true);
  };

  const handleSignOut = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/', { replace: true });
  };

  const contactMentorHref = 'mailto:support@mentology.com.au?subject=Contact%20Assigned%20Mentor';

  const submitReflection = async () => {
    if (!synthesis?.report) return;
    await chatApi.submitReflection(synthesis.report.cycle_number, reflection);
    setSynthesis(null);
    chatApi.synthesisStatus().then(setSynthesis);
  };

  const canSend = devBypassActive || (introComplete && (chatEnabled || localChatEnabled));

  if (synthesis?.required && synthesis.report && !synthesis.report.completed) {
    const report = JSON.parse(synthesis.report.report_json);
    return (
      <div className="workspace-screen" data-screen-id={workspaceScreenId}>
        <div className="workspace-util" ref={menuRef}>
          <button type="button" className="workspace-util-trigger" onClick={() => setMenuOpen((o) => !o)}>
            Menu
          </button>
        </div>
        <div className="workspace-logo-wrap">
          <img src={LOGO} alt="Mentology" className="workspace-logo brand-logo" />
        </div>
        <div className="workspace-reflection">
          <h2 className="workspace-reflection-title">Day 28 Synthesis</h2>
          <div className="workspace-bubble workspace-bubble-assistant scannable-bullet-text">
            {report.actionLearningLoops?.map((loop: { phase: string; summary: string }) => (
              <p key={loop.phase}><strong>{loop.phase}:</strong> {loop.summary}</p>
            ))}
          </div>
          <textarea
            className="input-field workspace-reflection-input"
            placeholder="Your evaluation response..."
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
          />
          <button type="button" className="btn-primary workspace-reflection-btn" onClick={submitReflection}>
            Submit Reflection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="workspace-screen" data-screen-id={workspaceScreenId}>
      <div className="workspace-util" ref={menuRef}>
        <button
          type="button"
          className="workspace-util-trigger"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
        >
          Menu
        </button>
        {menuOpen && (
          <div className="workspace-util-dropdown">
            {isEltTrack ? (
              <>
                <p className="workspace-util-item workspace-util-static">
                  Key: {activeClientKey}
                </p>
                <p className="workspace-util-item workspace-util-static">
                  {assignedAiMentorName}
                </p>
                <hr className="workspace-util-divider" />
                <button
                  type="button"
                  className="workspace-util-item"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/pro-setup');
                  }}
                >
                  Customize AI Mentor Settings
                </button>
                <button
                  type="button"
                  className="workspace-util-item"
                  onClick={handleCreateAppIcon}
                >
                  Create Desktop / Mobile App Icon
                </button>
                <button
                  type="button"
                  className="workspace-util-item"
                  onClick={handleShowQrCode}
                >
                  Show Mobile Access QR Code
                </button>
                <hr className="workspace-util-divider" />
                <a
                  className="workspace-util-item"
                  href={contactMentorHref}
                  onClick={() => setMenuOpen(false)}
                >
                  Contact Mentor
                </a>
                <button
                  type="button"
                  className="workspace-util-item"
                  onClick={handleSignOut}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <p className="workspace-util-item workspace-util-static">
                  Subscription: {subscriptionId}
                </p>
                <p className="workspace-util-item workspace-util-static">
                  Subscription days left: {subscriptionDaysLeft}
                </p>
                <button
                  type="button"
                  className="workspace-util-item"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/pro-setup');
                  }}
                >
                  Change my AI-Mentor Voice
                </button>
                <button type="button" className="workspace-util-item" onClick={() => setMenuOpen(false)}>
                  Renew subscription
                </button>
                <a
                  className="workspace-util-item"
                  href="mailto:support@mentology.com.au"
                  onClick={() => setMenuOpen(false)}
                >
                  Contact technical support
                </a>
                <button
                  type="button"
                  className="workspace-util-item"
                  onClick={() => {
                    setMenuOpen(false);
                    alert('Subscription cancellation will take effect at the end of the current billing window.');
                  }}
                >
                  Cancel subscription
                </button>
                <button type="button" className="workspace-util-item workspace-util-danger" onClick={handleDeleteFiles}>
                  Delete Files
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="workspace-logo-wrap">
        <img
          src={LOGO}
          alt="Mentology"
          className="workspace-logo brand-logo"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </div>

      <div className="workspace-body">
        <div className={`workspace-ai-box ${SPOKEN_TEXT_ATTRS.className}`}>
          <div
            ref={aiTextRef}
            className={`workspace-ai-text scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}
          >
            {introDisplay || (!introComplete && !playbackStarted.current ? ' ' : '')}
            {!introComplete && <span className="workspace-cursor">|</span>}
          </div>
        </div>

        <div className="workspace-thread">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`workspace-bubble workspace-bubble-${m.role} scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}
            >
              {m.content}
            </div>
          ))}
          {loading && <p className="workspace-bubble workspace-bubble-assistant scannable-bullet-text">Processing...</p>}
        </div>

        <form
          className="workspace-input-wrap"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSend && input.trim() && !loading) sendMessage();
          }}
        >
          <textarea
            className="workspace-chat-input"
            placeholder="Share your challenge and goals..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (canSend && input.trim() && !loading) sendMessage();
              }
            }}
            disabled={loading}
            aria-label="Mentee message input"
          />
          <div className="workspace-input-controls">
            <button
              type="submit"
              className="workspace-send-btn"
              disabled={loading || !canSend || !input.trim()}
            >
              Send
            </button>
            <button
              type="button"
              className={`workspace-mic-btn ${listening ? 'workspace-mic-active' : ''}`}
              onClick={toggleMic}
              disabled={loading}
              aria-label="Microphone"
              title="Microphone"
            >
              🎙️
            </button>
          </div>
        </form>
      </div>

      {qrModalOpen && (
        <div
          className="workspace-qr-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="workspace-qr-title"
          onClick={() => setQrModalOpen(false)}
        >
          <div
            className="workspace-qr-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="workspace-qr-title" className="section-header">Mobile Access QR Code</h2>
            <p className="workspace-qr-url scannable-bullet-text">{resolvePortalAccessUrl()}</p>
            <div className="workspace-qr-code-wrap" aria-hidden="true">
              <QRCodeSVG
                value={resolvePortalAccessUrl()}
                size={220}
                level="M"
                bgColor="#ffffff"
                fgColor="#000000"
                includeMargin
              />
            </div>
            <button type="button" className="btn-primary workspace-qr-close" onClick={() => setQrModalOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
